import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { ChronocodeVO } from '../../vo/core/chronocode.vo';
import { ChronocodeTemplateVO } from '../../vo/core/chronocode-template.vo';
import { ChronoCodeLevel } from '@wizeoo/akm-message-lib';
import { ChronoCodeLevelVO } from '../../vo/core/chronocodelevel.vo';
import { WizDateUtils, WizStringUtils } from '@wizeoo/akm-core-lib';
import { TimeGapVO } from './../../vo/core/timegap.vo';
import { TIME_UNIT } from '../../model/enums';
import { TranslatableLabelEntryVO } from '../../vo/common/translation/translatable-label-entry.vo';


/**
 * construction d'un nouveau chronocode sur la base d'un template
 */
export class ChronocodeGenerator {


  /* ################################################
            création d'un chronocode (complet)
   ################################################ */


  /** demande de création */
  public static addChronocode(model: Model): void {
    const template: ChronocodeTemplateVO = new ChronocodeTemplateVO(model);
    template.domain = model.domainCode;
    template.businessUnitCode = model.businessUnitCode;
    template.parentCode = ViewData.defaultChronoCode; // parentVO.code;
    template.startDate = WizDateUtils.dateToNumber(WizDateUtils.TODAY);
    template.level = 1;
    template.timeGapCode = TIME_UNIT.UNDEFINED;

    model.chronoCodeTemplate = template;
  }

  /* ################################################
          génération des chronocodes
   ################################################ */

  /** depuis le template, génère la flatList des chronocodes */
  public static generateChronocode(model: Model): ChronocodeVO[] {
    const result: ChronocodeVO[] = [];
    const mainChron: ChronocodeVO = this.generateMainLevel(model, model.chronoCodeTemplate); // génération du 1er niveau, c'est lui qui portera les levels
    result.push(mainChron);
    model.chronoCodeTemplate.addSimulated([mainChron]);

    if (model.chronoCodeTemplate.subLevels.length > 0) {
      this.feedSubChronos(model, model.chronoCodeTemplate, mainChron, model.chronoCodeTemplate.subLevels[0], result); // on commence le build sur le niveau 1
    }
    return result;
  }

  /**
   * Création d'un chronocode de 1er niveau
   * @return ChronocodeVO Le chronocode créé
   */
  private static generateMainLevel(model: Model, template: ChronocodeTemplateVO): ChronocodeVO {
    const result: ChronocodeVO = new ChronocodeVO(model);
    result.parentCode = template.parentCode;
    result.domain = template.domain;
    result.businessUnitCode = template.businessUnitCode;
    result.code = template.shortCode;
    result.rootCode = template.shortCode; // root = self sur les chronos de level 1
    result.shortCode = template.shortCode;
    result.level = template.level;
    result.order = 0;
    result.label = template.label;
    result.startDate = template.startDate;
    result.endDate = template.endDate;
    result.childrenRegex = null; // TODO contrôler sur sub chron, text + number
    result.enabled = true;
    result.deletable = true;

    // création des levels, ne pas oublier le 1er
    const levels: ChronoCodeLevel[] = [];
    let chronLevel: ChronoCodeLevel = new ChronoCodeLevel(); // création du level 1
    chronLevel.level = 1; // ils sont niveaux 2, on commence niveau 1
    chronLevel.domain = template.domain;
    chronLevel.businessUnitCode = template.businessUnitCode;
    chronLevel.timeGapCode = template.timeGapCode;
    chronLevel.label = template.label;
    chronLevel.parentCode = template.shortCode;
    levels.push(chronLevel);
    result.addLevel(new ChronoCodeLevelVO(model, chronLevel));

    template.subLevels.forEach((sub: ChronocodeTemplateVO) => {
      chronLevel = new ChronoCodeLevel();
      chronLevel.domain = template.domain;
      chronLevel.businessUnitCode = template.businessUnitCode;
      chronLevel.level = sub.level; // ils sont niveaux 2, on commence niveau 1
      chronLevel.timeGapCode = sub.timeGapCode;
      chronLevel.label = sub.label;
      chronLevel.parentCode = template.shortCode;
      levels.push(chronLevel);
      result.addLevel(new ChronoCodeLevelVO(model, chronLevel));
    });

    return result;
  }

  /** alimentation des enfants du chrono en cours de création */
  /**
   * alimentation récursive des chronocodes enfants
   * @param mainTemplate le template de niv1
   * @param parent le chronocode parent pour lequel on construit les enfants
   * @param toBuild le template de base pour la construction des enfants
   * @param coll la collection à alimenter
   */
  private static feedSubChronos(
    model: Model,
    mainTemplate: ChronocodeTemplateVO,
    parent: ChronocodeVO,
    toBuild: ChronocodeTemplateVO,
    coll: ChronocodeVO[]
  ): void {
    if (!!toBuild && toBuild.hasTimeGap) {
      const subResult: ChronocodeVO[] = this.buildChronocodeForLevel(model, parent, toBuild, coll); // construction des chronos du niveau toBuild
      toBuild.addSimulated(subResult);
      const nextTemplate: ChronocodeTemplateVO = mainTemplate.subLevelMap.get(toBuild.level + 1);
      if (!!nextTemplate && nextTemplate.hasTimeGap) {
        subResult.forEach((chro: ChronocodeVO) => {
          this.feedSubChronos(model, mainTemplate, chro, nextTemplate, coll);
        });
      }
    }
  }

  /**
   * génération de tous les chronocodes pour un niveau donnée
   * @param parent chrono parent
   * @param toBuild template de base pour la construction
   * @param coll collection à alimenter
   * @returns la coll du niveau, pour traitement récursif
   */
  private static buildChronocodeForLevel(
    model: Model,
    parent: ChronocodeVO,
    toBuild: ChronocodeTemplateVO,
    coll: ChronocodeVO[]
  ): ChronocodeVO[] {
    const levelPack: ChronocodeVO[] = [];

    let idx: number = 1; // indice de création
    const timegap: TimeGapVO = toBuild.timeGapVO; // le type de ventilation
    let startDate: Date = WizDateUtils.numberToDate(parent.startDate); // la date de début pour le découpage
    const endDate: Date = WizDateUtils.numberToDate(parent.endDate); // la date de fin pour la période
    // Les gaps entre les différentes start date à générer
    let dateGap: number = 0;
    let monthGap: number = 0;
    let yearGap: number = 0;

    // let delta: number = 0; // en cas de split undefined, ventiler le reliquat sur le dernier élément

    // Détermination du gap à appliquer entre chaque période
    if (timegap.code === TIME_UNIT.UNDEFINED) {
      const totalDays: number = WizDateUtils.dayDiff(startDate, endDate);
      const requestedChildren: number = toBuild.requestedChildren ? toBuild.requestedChildren : 1;
      dateGap = Math.ceil(totalDays / requestedChildren);
      // delta = totalDays - dateGap * requestedChildren;
    } else if (timegap.unit === TIME_UNIT.YEAR) {
      yearGap = timegap.value;
    } else if (timegap.unit === TIME_UNIT.MONTH) {
      monthGap = timegap.value;
    } else if (timegap.unit === TIME_UNIT.WEEK) {
      dateGap = timegap.value * 7;
    } else if (timegap.unit === TIME_UNIT.DAY) {
      dateGap = timegap.value;
    }

    // Gestion du caractère calendaire des période pour l'initialisation du découpage
    if (timegap.calendar) {
      let firstUncutStartDate: Date = startDate;
      // Année calendaire, commencer le 1er janvier
      if (timegap.unit === TIME_UNIT.YEAR) {
        if (startDate.getDate() !== 1 || startDate.getMonth() !== 0) {
          firstUncutStartDate = new Date(startDate.getFullYear() + 1, 0, 1);
        }
        // Mois calendaire, commencer le 1er du mois
      } else if (timegap.unit === TIME_UNIT.MONTH) {
        if (startDate.getDate() !== 1) {
          firstUncutStartDate = WizDateUtils.add(new Date(startDate.getFullYear(), startDate.getMonth(), 1), {
            months: 1,
          }) as Date;
        }
        // Mois calendaire, commencer le 1er jour de la semaine
      } else if (timegap.unit === TIME_UNIT.WEEK || (timegap.unit === TIME_UNIT.DAY && timegap.value === 7)) {
        // chercher le 1er lundi (TODO dimanche?) de la période
        firstUncutStartDate = model.chronocodeEngine.getNextWeekStartDate(firstUncutStartDate);
      }
      // Initialisation de la 1ère période tronquée si nécessaire
      if (WizDateUtils.isAfter(firstUncutStartDate, startDate) && WizDateUtils.isBefore(firstUncutStartDate, endDate)) {
        const halfCro: ChronocodeVO = this.feedChildChronocode(model,
          parent,
          toBuild,
          idx,
          startDate,
          WizDateUtils.add(firstUncutStartDate, { days: -1 }) as Date,
          true
        );
        coll.push(halfCro);
        levelPack.push(halfCro);
        idx++;
        startDate = firstUncutStartDate;
      }
    }

    let newChro: ChronocodeVO = null; // le chrono à générer
    if (toBuild.customPeriods && toBuild.customPeriods.length > 0) {
      toBuild.customPeriods.forEach((chro: ChronocodeVO) => {
        coll.push(chro);
        levelPack.push(chro);
        idx++;
      });
    } else {
      // On applique en boucle le gap selon le time gap.
      while (WizDateUtils.isBeforeOrSame(startDate, endDate)) {
        // Le prochain pas
        const nextStepDate: Date = WizDateUtils.add(startDate, {
          days: dateGap,
          months: monthGap,
          years: yearGap,
        }) as Date;
        // date de fin: la veille du pas suivant qui sera la prochaine startDate
        let theoricEndDate: Date = WizDateUtils.add(nextStepDate, { days: -1 }) as Date;
        if (!WizDateUtils.isBefore(theoricEndDate, endDate)) {
          theoricEndDate = endDate;
        }
        newChro = this.feedChildChronocode(model, parent, toBuild, idx, startDate, theoricEndDate);
        coll.push(newChro);
        levelPack.push(newChro);
        idx++;
        startDate = nextStepDate;
      }
      // contrôle de cohérence sur découpage. Le dernier élément doit correspondre à la date de fin si undefined
      if (newChro.endDate < parent.endDate) {
        // console.log('delta', newChro.endDate, parent.endDate);
        // on fait simple, il suffit de prendre la date de fin réelle
        newChro.endDate = parent.endDate;
      }
    }
    return levelPack;
  }

  /**
   * Initialisation d'un enfant de chronocode
   * @param parent chrono parent pour rattachement
   * @param template template pour construction
   * @param idx indice dans le parent
   * @param startDate date de début
   * @param endDate date de fin
   * @param isHalf c'est une plage tronquée
   * @returns le chrono construit
   */
  private static feedChildChronocode(
    model: Model,
    parent: ChronocodeVO,
    template: ChronocodeTemplateVO,
    idx: number,
    startDate: Date,
    endDate: Date,
    isHalf: boolean = false
  ): ChronocodeVO {
    const result: ChronocodeVO = new ChronocodeVO(model);
    const baseIndex = 100;

    result.simulatedParent = parent; // alimentation du parent de la simulation, pour édition des périodes
    result.template = template;

    result.parentCode = parent.code;
    result.domain = parent.domain;
    result.businessUnitCode = parent.businessUnitCode;

    result.startDate = WizDateUtils.dateToNumber(startDate);
    result.endDate = WizDateUtils.dateToNumber(endDate);

    result.shortCode = this.generateShortCode(template.codeRegex, idx, parent, result, isHalf); // APRES les dates, besoin pour calcul semaines
    // result.code = parent.code + ViewData.CODE_LEVEL_SEPARATOR + result.shortCode;
    result.rootCode = parent.rootCode;
    result.code = parent.code + ViewData.CODE_LEVEL_SEPARATOR + (baseIndex + idx).toString();

    result.level = template.level;
    result.order = idx;

    result.enabled = true;
    result.deletable = true;

    // Gérer plus proprement et distinctement les 2 libellés
    let entry: TranslatableLabelEntryVO = new TranslatableLabelEntryVO();
    entry.languageCode = model.buLocaleCode;
    entry.label = result.shortCode;
    result.labelVO.addEntry(entry);
    const currentLanguage: string = model.currentLocaleCode;
    if (currentLanguage !== entry.languageCode) {
      entry = new TranslatableLabelEntryVO();
      entry.languageCode = currentLanguage;
      entry.label = result.shortCode;
      result.labelVO.addEntry(entry);
    }

    // parent.addChild(result); // ajout au parent, pour restitution de l'arbre

    return result;
  }

  /**
   * Génération d'un shortCode pour un niveau et un index
   * @param regexStr pseudo regex à modifier
   * @param pIndex l'index
   * @param pParentVO chronocode parent
   * @param isHalf c'est une plage tronquée
   */
  private static generateShortCode(
    regexStr: string,
    pIndex: number,
    pParentVO: ChronocodeVO,
    chrono: ChronocodeVO,
    isHalf: boolean = false
  ): string {
    const regexp: RegExp = new RegExp('(\\[P_[^\\[]+\\])', 'g');
    const matches = regexStr.match(regexp);
    let result = this.replaceParams(regexStr, pParentVO, pIndex, matches, chrono);
    if(isHalf) {
      result+='(2)';
    }
    return result;
  }

  private static replaceParams(
    model: string,
    parentVO: ChronocodeVO,
    level: number,
    params: RegExpMatchArray,
    child: ChronocodeVO
  ): string {
    let result: string = model;
    for (const match of params) {
      result = this.replaceParam(result, parentVO, level, match, child);
    }
    return result;
  }

  /**
   * Remplacement d'un paramètre d'un modèle de génération de code Chronocode
   * @param model Modèle à modifier
   * @param parentVO Chronocode parent
   * @param level Le niveau de l'enfant par rapport à son parent
   * @param params Les paramètres à remplacer
   */
  private static replaceParam(
    model: string,
    parentVO: ChronocodeVO,
    level: number,
    param: string,
    chrono: ChronocodeVO
  ): string {
    const jsonRegexp: RegExp = new RegExp('{.*}', 'g');
    if (param.indexOf('P_INDEX') >= 0) {
      const jsonMatches: RegExpMatchArray = param.match(jsonRegexp);
      const paramStr: string = jsonMatches && jsonMatches.length > 0 ? jsonMatches[0] : null;
      if (paramStr) {
        const obj: any = JSON.parse(paramStr);
        const format: string = obj.format;
        model = WizStringUtils.replace(model, param, this.formatChronocodeLevel(level, format, chrono));
      }
    } else if (param.indexOf('P_PARENT') >= 0) {
      const jsonMatches: RegExpMatchArray = param.match(jsonRegexp);
      const paramStr: string = jsonMatches && jsonMatches.length > 0 ? jsonMatches[0] : null;
      if (paramStr) {
        const obj: any = JSON.parse(paramStr);
        const paramField: string = obj.field;
        let valueStr: string = '';
        if (paramField) {
          const value: any = parentVO[paramField];
          if (typeof value === 'string' || value instanceof String) {
            valueStr = value as string;
            model = WizStringUtils.replace(model, param, valueStr);
          } else if (typeof value === 'number' || value instanceof Number) {
            const format: string = obj.format;
            model = WizStringUtils.replace(model, param, this.formatChronocodeLevel(value as number, format, chrono));
          }
        }
      }
    }
    return model;
  }

  /**
   * Formatage d'un niveau
   * @param level niveau
   * @param format format
   * @return string Le niveau formaté
   */
  public static formatChronocodeLevel(level: number, format: string, chrono: ChronocodeVO): string {
    let result: string = '';
    if (!format) {
      format = '1';
    }
    switch (format) {
      case '1':
      case '11':
        result = level.toString();
        if (format === '11' && result.length === 1) {
          result = '0' + result;
        }
        break;
      case 'a':
      case 'A':
        result = this.toStringNumerotation(level);
        if (format === 'A') {
          result = result.toUpperCase();
        }
        break;
      case 'DefaultWeek':
        // si le nommage des semaines est par défaut
        const weekNumber = WizDateUtils.weekNumber(chrono.startDate); // après publication lib 2.3.27, utiliser la méthode  WizDateUtils.weekNumber(chrono.startDate);
        // ex: s1-2021
        result = weekNumber[0].toString() + '-' + weekNumber[1].toString();
        break;
    }
    return result;
  }

  /**
   * Transformation d'un nombre en numérotation alpha (style colonne excel)
   * @param n Le nombre à transformer
   * @return string Le nombre formaté
   */
  public static toStringNumerotation(n: number) {
    n--; // on veut a = 1, ici a correspondra à 0
    const ordA = 'a'.charCodeAt(0); // position du a
    const ordZ = 'z'.charCodeAt(0); // position du z
    const len = ordZ - ordA + 1; // longueur de l'alphabet (26 logiquement)

    // Consruction de la chaîne de caractère
    let s: string = '';
    while (n >= 0) {
      s = String.fromCharCode((n % len) + ordA) + s;
      n = Math.floor(n / len) - 1;
    }
    return s;
  }

  /* ################################################
          utilitaires autres objets (vision)
   ################################################ */


  /**
   * retourne tous les enfants du niveau donnée pour un chronocode root
   * @param chronocode la source
   * @param requiredLevel le niveau recherché
   */
  public static getChildrenByLevel(chronocode: ChronocodeVO, requiredLevel: number): ChronocodeVO[] {
    const result: ChronocodeVO[] = [];
    this.recurseChildrenByLevel(chronocode, requiredLevel, result);
    return result;
  }
  private static recurseChildrenByLevel(chronocode: ChronocodeVO, requiredLevel: number, result: ChronocodeVO[]) {
    if (chronocode.level === requiredLevel) {
      result.push(chronocode);
    } else {
      chronocode.children.forEach((child: ChronocodeVO) => {
        this.recurseChildrenByLevel(child, requiredLevel, result);
      });
    }
  }


}
